/**
 * FishPrawnCrab chip layer — replaces
 * packages/amgfishprawncrab_v2/lib/presentation/flame/main_flame_game.dart.
 *
 * Coordinate system: 1792×828 reference (PixiLayer's logical size).
 *
 * Bet-area scatter rectangles come from `_chipsPosition` in
 * main_flame_game.dart:24-47 — `ChipRandomPosition(minX%, maxXpx, minY%, maxYpx)`.
 * Coordinates: top-left = minX*REF_WIDTH + random[0..maxXpx], same for Y.
 *
 * Player origin (chips fly FROM): (size.x/2, size.y * 0.95) = (896, 786.6).
 * Dealer target (lose):  (size.x*0.5, size.y * 0.2) = (896, 165.6).
 * Player target (win):   (size.x*0.5, size.y * 1.1) = (896, 910.8).
 * Center target (tie):   (size.x*0.5, size.y * 0.55) ± 2% random.
 *
 * Animations (mirror chips_sprite.dart):
 *   Placement: 0.35 s linear.
 *   Move (win/lose/center): 0.5 s, 0.02 s × index stagger, fade alpha → 0.2.
 */
import { Container } from 'pixi.js';
import { ChipSprite, preloadCoinTextures } from './chipSprite';
import { Easings, tween } from './tween';
import { REF_HEIGHT, REF_WIDTH } from '../config';

interface XY {
  x: number;
  y: number;
}

interface ScatterRect {
  /** Top-left x as fraction of REF_WIDTH. */
  minXFrac: number;
  /** Scatter range in px. */
  rangeX: number;
  minYFrac: number;
  rangeY: number;
}

/**
 * Bet positions from main_flame_game.dart:24-47.
 * Source rows in Flutter use ChipRandomPosition(minX%, maxXpx, minY%, maxYpx).
 */
const SCATTER: ReadonlyMap<number, ScatterRect> = new Map([
  [277, { minXFrac: 0.271, rangeX: 150, minYFrac: 0.265, rangeY: 65 }],
  [272, { minXFrac: 0.400, rangeX: 360, minYFrac: 0.265, rangeY: 65 }],
  [278, { minXFrac: 0.645, rangeX: 150, minYFrac: 0.265, rangeY: 65 }],
  [275, { minXFrac: 0.271, rangeX: 150, minYFrac: 0.38,  rangeY: 65 }],
  [273, { minXFrac: 0.390, rangeX: 150, minYFrac: 0.38,  rangeY: 65 }],
  [276, { minXFrac: 0.52,  rangeX: 150, minYFrac: 0.38,  rangeY: 65 }],
  [274, { minXFrac: 0.645, rangeX: 150, minYFrac: 0.38,  rangeY: 65 }],
  [279, { minXFrac: 0.256, rangeX: 120, minYFrac: 0.54,  rangeY: 35 }],
  [280, { minXFrac: 0.360, rangeX: 120, minYFrac: 0.54,  rangeY: 35 }],
  [281, { minXFrac: 0.468, rangeX: 120, minYFrac: 0.54,  rangeY: 35 }],
  [282, { minXFrac: 0.575, rangeX: 120, minYFrac: 0.54,  rangeY: 35 }],
  [283, { minXFrac: 0.682, rangeX: 120, minYFrac: 0.54,  rangeY: 35 }],
  [284, { minXFrac: 0.256, rangeX: 120, minYFrac: 0.625, rangeY: 35 }],
  [285, { minXFrac: 0.360, rangeX: 120, minYFrac: 0.625, rangeY: 35 }],
  [286, { minXFrac: 0.468, rangeX: 120, minYFrac: 0.625, rangeY: 35 }],
  [287, { minXFrac: 0.575, rangeX: 120, minYFrac: 0.625, rangeY: 35 }],
  [288, { minXFrac: 0.682, rangeX: 120, minYFrac: 0.625, rangeY: 35 }],
  [289, { minXFrac: 0.256, rangeX: 120, minYFrac: 0.710, rangeY: 35 }],
  [290, { minXFrac: 0.360, rangeX: 120, minYFrac: 0.710, rangeY: 35 }],
  [331, { minXFrac: 0.468, rangeX: 120, minYFrac: 0.710, rangeY: 35 }],
  [333, { minXFrac: 0.575, rangeX: 120, minYFrac: 0.710, rangeY: 35 }],
  [334, { minXFrac: 0.682, rangeX: 120, minYFrac: 0.710, rangeY: 35 }],
]);

const PLAYER_ORIGIN: XY = { x: REF_WIDTH / 2, y: REF_HEIGHT * 0.95 };
const DEALER_TARGET: XY = { x: REF_WIDTH * 0.5, y: REF_HEIGHT * 0.2 };
const PLAYER_WIN_TARGET: XY = { x: REF_WIDTH * 0.5, y: REF_HEIGHT * 1.1 };
const CENTER_TARGET: XY = { x: REF_WIDTH * 0.5, y: REF_HEIGHT * 0.55 };

interface ActiveChip {
  betItemId: number;
  sprite: ChipSprite;
}

export class ChipLayer {
  private readonly root: Container;
  private active: ActiveChip[] = [];

  constructor(parent: Container) {
    this.root = new Container();
    this.root.label = 'ChipLayer';
    parent.addChild(this.root);
    void preloadCoinTextures();
  }

  /**
   * Add a chip at a random scatter point inside the bet area.
   * Mirrors `assignNewChip(betItemId, chipIndex)` in main_flame_game.dart:100-118.
   */
  placeChip(betItemId: number, chipValue: number, chipIndex: number): void {
    const rect = SCATTER.get(betItemId);
    const target: XY = rect
      ? {
          x: rect.minXFrac * REF_WIDTH + Math.random() * rect.rangeX,
          y: rect.minYFrac * REF_HEIGHT + Math.random() * rect.rangeY,
        }
      : { x: REF_WIDTH * 0.5, y: REF_HEIGHT * 0.5 };

    const sprite = new ChipSprite(chipValue, chipIndex);
    sprite.position.set(PLAYER_ORIGIN.x, PLAYER_ORIGIN.y);
    this.root.addChild(sprite);

    const from = { ...PLAYER_ORIGIN };
    tween({
      durationMs: 350, // ChipsSprite.onLoad EffectController(duration: 0.35)
      easing: Easings.linear,
      onUpdate: (t) => {
        sprite.position.set(
          from.x + (target.x - from.x) * t,
          from.y + (target.y - from.y) * t,
        );
      },
    });

    this.active.push({ betItemId, sprite });
  }

  /**
   * Move all chips to the center (visual feedback after dice reveal, before
   * payout). Mirrors `moveAllToCenter` in main_flame_game.dart:144-148.
   */
  animateAllToCenter(): void {
    const list = this.active;
    list.forEach((c, i) => {
      const offsetX = REF_WIDTH * 0.02 * (Math.random() - 0.5);
      const offsetY = REF_HEIGHT * 0.02 * (Math.random() - 0.5);
      const tgt: XY = { x: CENTER_TARGET.x + offsetX, y: CENTER_TARGET.y + offsetY };
      this.flyTo(c, tgt, i, false);
    });
  }

  /** All chips fly to the player target (full win). */
  animateAllToPlayer(): void {
    const list = this.active;
    this.active = [];
    list.forEach((c, i) => this.flyTo(c, PLAYER_WIN_TARGET, i, true));
    this.scheduleDestroyAll(list, 800);
  }

  /** All chips fly to the dealer target (full loss). */
  animateAllToDealer(): void {
    const list = this.active;
    this.active = [];
    list.forEach((c, i) => this.flyTo(c, DEALER_TARGET, i, true));
    this.scheduleDestroyAll(list, 800);
  }

  /**
   * Half/half: first half of chips go to dealer, the second half to player.
   * Mirrors `moveHalfHalf` in main_flame_game.dart:162-171.
   */
  animateHalfHalf(): void {
    const list = this.active;
    this.active = [];
    const half = Math.ceil(list.length / 2);
    list.forEach((c, i) => {
      if (i < half) this.flyTo(c, DEALER_TARGET, i, true);
      else this.flyTo(c, PLAYER_WIN_TARGET, i - half, true);
    });
    this.scheduleDestroyAll(list, 800);
  }

  /**
   * Animate only chips on a specific bet area to the dealer (lost bets).
   * Mirrors `movePosChipsToDealer(betItemId)` in main_flame_game.dart:128-134.
   */
  animateBetToDealer(betItemId: number): void {
    const matching = this.active.filter((c) => c.betItemId === betItemId);
    this.active = this.active.filter((c) => c.betItemId !== betItemId);
    matching.forEach((c, i) => this.flyTo(c, DEALER_TARGET, i, true));
    this.scheduleDestroyAll(matching, 800);
  }

  /**
   * Animate only chips on a specific bet area to the player (winning bets).
   */
  animateBetToPlayer(betItemId: number): void {
    const matching = this.active.filter((c) => c.betItemId === betItemId);
    this.active = this.active.filter((c) => c.betItemId !== betItemId);
    matching.forEach((c, i) => this.flyTo(c, PLAYER_WIN_TARGET, i, true));
    this.scheduleDestroyAll(matching, 800);
  }

  hardClear(): void {
    for (const c of this.active) c.sprite.destroy();
    this.active = [];
  }

  hasAnyChips(): boolean {
    return this.active.length > 0;
  }

  // -----------------------------------------------------------------

  private flyTo(c: ActiveChip, target: XY, index: number, fade: boolean): void {
    const fromX = c.sprite.position.x;
    const fromY = c.sprite.position.y;
    const fromAlpha = c.sprite.alpha;
    const delayMs = 20 * index;

    window.setTimeout(() => {
      tween({
        durationMs: 500,
        easing: Easings.linear,
        onUpdate: (t) => {
          c.sprite.position.set(
            fromX + (target.x - fromX) * t,
            fromY + (target.y - fromY) * t,
          );
          if (fade) {
            c.sprite.alpha = fromAlpha + (0.2 - fromAlpha) * t;
          }
        },
      });
    }, delayMs);
  }

  private scheduleDestroyAll(list: ActiveChip[], delayMs: number): void {
    window.setTimeout(() => {
      for (const c of list) {
        try {
          c.sprite.destroy();
        } catch {
          /* already destroyed */
        }
      }
    }, delayMs);
  }
}
